import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  api,
  Technique,
  Problem,
  PracticeSession,
  TechniqueProgress,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Flame,
  Target,
  Shuffle,
  CheckCircle,
  XCircle,
  Trophy,
  Check,
  X,
  ChevronsUpDown,
  ArrowRight,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { featureFlags, userHasPremiumAccess } from "../config/featureFlags";
import { SealCelebration } from "@/components/SealCelebration";
import PremiumUpgradeDialog from "@/components/PremiumUpgradeDialog";

// All problem tracking is now done locally - no per-problem API calls
// Stats are aggregated and sent once when the session completes

interface ProblemAttempt {
  problem: Problem;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  timeTaken: number;
}

// Rotating praise so correct answers feel like a celebration, not a checkmark
const PRAISE_MESSAGES = [
  "You got it! 🎉",
  "Nice one! ⭐",
  "Awesome! 🚀",
  "Great job! 💪",
  "Super! 🌟",
];

export default function PracticeArena() {
  const [searchParams] = useSearchParams();
  const techniqueIdFromUrl =
    searchParams.get("techniqueId") || searchParams.get("technique");
  const assignmentIdFromUrl = searchParams.get("assignment");
  const lessonIdFromUrl = searchParams.get("lessonId");
  const fromLesson = searchParams.get("from") === "lesson";
  const returnToFromUrl = searchParams.get("returnTo");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const isPremiumUser = userHasPremiumAccess(user);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("");

  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [userProgress, setUserProgress] = useState<TechniqueProgress[]>([]);
  const [selectedTechnique, setSelectedTechnique] = useState<string>(
    techniqueIdFromUrl || "",
  );
  const [techniqueComboOpen, setTechniqueComboOpen] = useState(false);
  const modeFromUrl = searchParams.get("mode") as
    | "technique"
    | "beat-the-clock"
    | null;
  const initialMode =
    modeFromUrl === "technique"
      ? "technique_drill"
      : modeFromUrl === "beat-the-clock"
        ? "beat_the_clock"
        : "technique_drill";
  const [mode, setMode] = useState<
    "technique_drill" | "mixed_challenge" | "beat_the_clock" | "sample_papers"
  >(initialMode);
  const [problemCount, setProblemCount] = useState<number>(
    fromLesson ? 20 : 10,
  );
  const [session, setSession] = useState<PracticeSession | null>(null);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState<{
    correct: boolean;
    message: string;
    correctAnswer?: string;
  } | null>(null);
  const [combo, setCombo] = useState(0);
  const [problemStartTime, setProblemStartTime] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(120);
  const [problemAttempts, setProblemAttempts] = useState<ProblemAttempt[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loadingProblem, setLoadingProblem] = useState(false);
  const [problemQueue, setProblemQueue] = useState<Problem[]>([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState<number>(0);
  const [isPersonalBest, setIsPersonalBest] = useState<boolean>(false);
  const [previousBest, setPreviousBest] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [masteredTechniques, setMasteredTechniques] = useState<
    TechniqueProgress[]
  >([]);
  const [currentDifficulty, setCurrentDifficulty] = useState<
    "easy" | "medium" | "hard"
  >("medium");
  const [xpEarned, setXpEarned] = useState<number>(0);
  const [xpAwarded, setXpAwarded] = useState<boolean>(true);
  const [dailyXpLimitReached, setDailyXpLimitReached] =
    useState<boolean>(false);
  const [lessonConfig, setLessonConfig] = useState<any>(null);
  const [showSealCelebration, setShowSealCelebration] = useState(false);
  const [sealedTechniqueName, setSealedTechniqueName] = useState<string>("");
  const [initialMasteryLevel, setInitialMasteryLevel] = useState<string | null>(null);
  const answerInputRef = useRef<HTMLInputElement>(null);
  const hasAutoStarted = useRef(false);
  const seenQuestions = useRef<Set<string>>(new Set());
  const problemAttemptsRef = useRef<ProblemAttempt[]>([]); // Sync ref for accurate counts
  // Auto-focus input when new problem loads  useEffect(() => {    if (currentProblem && !showResults && !feedback) {      answerInputRef.current?.focus();    }  }, [currentProblem, showResults, feedback]);

  useEffect(() => {
    const loadTechniques = async () => {
      try {
        const data = await api.getTechniques({ view: "list" }); // Fetch minimal data for dropdown
        setTechniques(data);
        if (
          techniqueIdFromUrl &&
          data.find((t) => t.id === techniqueIdFromUrl)
        ) {
          setSelectedTechnique(techniqueIdFromUrl);
        }
      } catch (error) {
        console.error("Failed to load techniques:", error);
      }
    };

    const loadUserProgress = async () => {
      try {
        const progress = await api.getUserProgress();
        setUserProgress(progress);
        // Filter for mastered techniques (intermediate, advanced, or expert)
        const mastered = progress.filter((p) =>
          ["intermediate", "advanced", "expert"].includes(p.mastery_level),
        );
        setMasteredTechniques(mastered);
      } catch (error) {
        console.error("Failed to load user progress:", error);
      }
    };

    loadTechniques();
    loadUserProgress();
  }, [techniqueIdFromUrl]);

  // Filter techniques based on user role:
  // - Teachers see all techniques
  // - Students see only techniques they have practiced (have progress on)
  const availableTechniques = useMemo(() => {
    if (isTeacher) {
      // Teachers can see all techniques
      return techniques;
    }
    // Students only see techniques they have completed/practiced
    const practicedTechniqueIds = new Set(
      userProgress.map((p) => p.technique_id),
    );
    return techniques.filter((t) => practicedTechniqueIds.has(t.id));
  }, [techniques, userProgress, isTeacher]);

  // Get lesson config from navigation state when coming from a lesson
  useEffect(() => {
    if (fromLesson && location.state?.lessonConfig) {
      const config = location.state.lessonConfig;
      setLessonConfig(config);
      // Also set the difficulty from lesson config
      if (config?.difficulty) {
        const difficulty = config.difficulty;
        if (
          difficulty === "easy" ||
          difficulty === "medium" ||
          difficulty === "hard"
        ) {
          setCurrentDifficulty(difficulty);
        }
      }
    }
  }, [fromLesson, location.state]);

  // Auto-start session when coming from a lesson
  useEffect(() => {
    if (
      fromLesson &&
      techniqueIdFromUrl &&
      !session &&
      techniques.length > 0 &&
      !hasAutoStarted.current
    ) {
      console.log("[Practice] Auto-starting session from lesson");
      hasAutoStarted.current = true;
      startSession();
    }
  }, [fromLesson, techniqueIdFromUrl, session, techniques]);

  // Timer for beat_the_clock mode (countdown)
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (
      session &&
      mode === "beat_the_clock" &&
      timeRemaining > 0 &&
      !feedback
    ) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // End session when timer runs out and show results
            handleEndSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [session, mode, timeRemaining, feedback]);

  const startSession = async () => {
    console.log(
      "[Practice] Starting session with mode:",
      mode,
      "technique:",
      selectedTechnique,
    );
    setLoadingProblem(true);

    // Capture initial mastery level for seal celebration detection
    if (selectedTechnique && (mode === "technique_drill" || mode === "beat_the_clock")) {
      const currentProgress = userProgress.find(p => p.technique_id === selectedTechnique);
      setInitialMasteryLevel(currentProgress?.mastery_level || "none");
      console.log("[Practice] Initial mastery level:", currentProgress?.mastery_level || "none");
    }

    try {
      console.log("[Practice] About to call api.startPracticeSession...");
      const newSession = await api.startPracticeSession(
        mode,
        mode === "technique_drill" || mode === "beat_the_clock"
          ? selectedTechnique
          : undefined,
        assignmentIdFromUrl || undefined,
      );
      console.log("[Practice] Session created:", newSession);

      if (!newSession) {
        console.error("[Practice] ERROR: Session is null!");
        toast({
          title: "Oops! Something went wrong 😅",
          description: "We couldn't start your training. Please try again!",
        });
        setLoadingProblem(false);
        return;
      }

      setSession(newSession);
      setTimeRemaining(120);
      setCombo(0);
      setProblemAttempts([]);
      problemAttemptsRef.current = []; // Clear ref too
      setShowResults(false);

      // Pre-load problems for technique_drill and beat_the_clock modes
      if (
        (mode === "technique_drill" || mode === "beat_the_clock") &&
        selectedTechnique
      ) {
        console.log("[Practice] Pre-loading problems...");
        // Use user-selected problem count for technique_drill, more for timed mode
        const count = mode === "beat_the_clock" ? 50 : problemCount;
        // For Challenge lessons with multiple problem types, pass as comma-separated string
        const problemType =
          lessonConfig?.problem_types?.length > 1
            ? lessonConfig.problem_types.join(",")
            : lessonConfig?.problem_types?.[0];
        const problems = await api.generateProblems(
          selectedTechnique,
          count,
          lessonConfig?.difficulty || "medium",
          problemType,
        );
        console.log("[Practice] Loaded", problems.length, "problems");
        setProblemQueue(problems);
        setCurrentProblemIndex(0);
        setCurrentProblem(problems[0]);
        setProblemStartTime(Date.now());
        setUserAnswer("");
        setFeedback(null);
      } else {
        // For other modes, load one problem at a time (existing behavior)
        await loadNextProblem(newSession);
      }

      console.log("[Practice] Session started successfully");
    } catch (error) {
      console.error("[Practice] Failed to start session:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (
        errorMessage.includes("not yet implemented") ||
        errorMessage.includes("being built")
      ) {
        toast({
          title: "Coming soon! 🛠️",
          description:
            "This technique is still being built. Check back soon!",
        });
      } else {
        toast({
          title: "Oops! Something went wrong 😅",
          description: "We couldn't start your training. Please try again!",
        });
      }
    } finally {
      setLoadingProblem(false);
    }
  };

  const loadNextProblem = async (_currentSession: PracticeSession) => {
    console.log("[Practice] Loading next problem...");
    setLoadingProblem(true);
    try {
      let techniqueId = selectedTechnique;

      if (mode === "mixed_challenge") {
        // Only use mastered techniques for mixed challenge
        if (masteredTechniques.length === 0) {
          toast({
            title: "Master a form first! 💪",
            description:
              "Train single techniques in Form Practice to unlock Combined Forms.",
          });
          setSession(null);
          setCurrentProblem(null);
          setLoadingProblem(false);
          return;
        }
        const randomMastered =
          masteredTechniques[
            Math.floor(Math.random() * masteredTechniques.length)
          ];
        techniqueId = randomMastered.technique_id;
      }

      console.log(
        "[Practice] Generating problem for technique:",
        techniqueId,
        "with difficulty:",
        currentDifficulty,
      );

      // Try to get a unique problem (max 3 attempts)
      let problem: Problem | null = null;
      let attempts = 0;

      // For Challenge lessons with multiple problem types, pass as comma-separated string
      const problemType =
        lessonConfig?.problem_types?.length > 1
          ? lessonConfig.problem_types.join(",")
          : lessonConfig?.problem_types?.[0];

      while (attempts < 3) {
        problem = await api.generateProblem(
          techniqueId,
          lessonConfig?.difficulty || currentDifficulty,
          problemType,
        );
        if (!seenQuestions.current.has(problem.question)) {
          break;
        }
        console.log("[Practice] Duplicate question received, retrying...");
        attempts++;
      }

      if (!problem) throw new Error("Failed to generate problem");

      // Add to seen set
      seenQuestions.current.add(problem.question);

      console.log("[Practice] Problem generated:", problem);

      setCurrentProblem(problem);
      setProblemStartTime(Date.now());
      setUserAnswer("");
      setFeedback(null);
      setSubmitting(false);
      // Explicitly focus the input for next problem
      setTimeout(() => answerInputRef.current?.focus(), 0);
    } catch (error) {
      console.error("[Practice] Failed to load problem:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (
        errorMessage.includes("not yet implemented") ||
        errorMessage.includes("being built")
      ) {
        toast({
          title: "Coming soon! 🛠️",
          description:
            "This technique is still being built, so we ended your session. Check back soon!",
        });
      } else {
        toast({
          title: "Oops! Something went wrong 😅",
          description:
            "We had trouble loading the next problem, so we ended your session.",
        });
      }

      setSession(null);
      setCurrentProblem(null);
    } finally {
      setLoadingProblem(false);
    }
  };

  const submitAnswer = async (answerOverride?: string) => {
    if (!session || !currentProblem || submitting) return;

    setSubmitting(true); // Disable button immediately

    const answerToSubmit = answerOverride || userAnswer;
    const timeTaken = (Date.now() - problemStartTime) / 1000;
    const normalizeAnswer = (s: string) => s.replace(/\s+/g, "").toLowerCase();
    // Grid-in style comparison: accept equivalent numeric forms
    // (e.g. "1/2", "0.5" and ".5" all match an answer of 0.5)
    const parseNumeric = (s: string): number | null => {
      const t = s.replace(/\s+/g, "").replace(/,/g, "");
      const frac = t.match(/^(-?\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
      if (frac) {
        const den = parseFloat(frac[2]);
        return den === 0 ? null : parseFloat(frac[1]) / den;
      }
      return /^-?(?:\d+\.?\d*|\.\d+)$/.test(t) ? parseFloat(t) : null;
    };
    const userNum = parseNumeric(answerToSubmit);
    const correctNum = parseNumeric(currentProblem.answer);
    const isCorrect =
      userNum !== null && correctNum !== null
        ? Math.abs(userNum - correctNum) < 1e-9
        : normalizeAnswer(answerToSubmit) === normalizeAnswer(currentProblem.answer);

    console.log("[Practice] Recording answer locally (no API call)...");
    console.log("[Validation]", {
      question: currentProblem.question,
      userAnswer: answerToSubmit.trim(),
      correctAnswer: currentProblem.answer.trim(),
      isCorrect: isCorrect,
      timeTaken: timeTaken,
    });

    // Record this attempt locally - no API call needed until session completes
    const attempt: ProblemAttempt = {
      problem: currentProblem,
      userAnswer: answerToSubmit,
      correctAnswer: currentProblem.answer,
      isCorrect: isCorrect,
      timeTaken: timeTaken,
    };
    // Update ref immediately (sync) - used by handleEndSession for accurate counts
    problemAttemptsRef.current = [...problemAttemptsRef.current, attempt];
    setProblemAttempts(problemAttemptsRef.current);

    const updatedSession = {
      ...session,
      problems_attempted: session.problems_attempted + 1,
      problems_correct: session.problems_correct + (isCorrect ? 1 : 0),
    };
    setSession(updatedSession);

    // Adaptive difficulty for mixed_challenge mode
    if (mode === "mixed_challenge") {
      if (isCorrect) {
        // Increase difficulty on correct answer
        if (currentDifficulty === "easy") setCurrentDifficulty("medium");
        else if (currentDifficulty === "medium") setCurrentDifficulty("hard");
      } else {
        // Decrease difficulty on incorrect answer
        if (currentDifficulty === "hard") setCurrentDifficulty("medium");
        else if (currentDifficulty === "medium") setCurrentDifficulty("easy");
      }
    }

    const newCombo = isCorrect ? combo + 1 : 0;
    setCombo(newCombo);

    const instantMode = mode === "technique_drill" || mode === "beat_the_clock";

    if (isCorrect) {
      // Correct: celebrate briefly, then move on automatically
      setFeedback({
        correct: true,
        message: PRAISE_MESSAGES[newCombo % PRAISE_MESSAGES.length],
      });

      if (instantMode) {
        setTimeout(() => advanceToNext(updatedSession), 600);
      } else {
        setTimeout(() => loadNextProblem(updatedSession), 600);
      }
    } else {
      // Wrong: pause so the correct answer can actually be read and learned.
      // Advancing happens from the "Keep Going" button (Enter also works via autoFocus).
      // In Time Trial the countdown is paused while feedback is showing, so no time is lost.
      setFeedback({
        correct: false,
        message: "Almost! The answer is",
        correctAnswer: currentProblem.answer,
      });
    }
  };

  // Move to the next pre-loaded problem, or end the session when done
  const advanceToNext = (updatedSession: PracticeSession) => {
    if (
      mode === "technique_drill" &&
      updatedSession.problems_attempted >= problemCount
    ) {
      console.log("[Practice] Session complete, ending...");
      handleEndSession();
      return;
    }

    const nextIndex = currentProblemIndex + 1;
    if (problemQueue.length > 0 && nextIndex < problemQueue.length) {
      setCurrentProblemIndex(nextIndex);
      setCurrentProblem(problemQueue[nextIndex]);
      setProblemStartTime(Date.now());
      setUserAnswer("");
      setFeedback(null);
      setSubmitting(false); // Re-enable submit button for next problem
      // Explicitly focus the input for next problem
      setTimeout(() => answerInputRef.current?.focus(), 0);
    } else {
      console.log("[Practice] No more problems in queue, ending session");
      handleEndSession();
    }
  };

  // Continue after reviewing a wrong answer
  const continueAfterWrong = () => {
    if (!session) return;
    if (mode === "mixed_challenge") {
      loadNextProblem(session);
    } else {
      advanceToNext(session);
    }
  };

  const handleEndSession = async () => {
    if (!session) return;

    console.log("[Practice] Ending session...");
    try {
      // Read from ref (sync) to get accurate counts - state may be stale due to async updates
      const allAttempts = problemAttemptsRef.current;
      const totalAttempted = allAttempts.length;
      const totalCorrect = allAttempts.filter((a) => a.isCorrect).length;
      const totalTime = allAttempts.reduce((sum, a) => sum + a.timeTaken, 0);

      console.log(
        `[Practice] Submitting aggregated stats: ${totalCorrect}/${totalAttempted} correct in ${totalTime.toFixed(1)}s`,
      );

      // Complete session with aggregated stats (single API call)
      const completedSession = await api.completePracticeSession(
        session.id,
        totalAttempted,
        totalCorrect,
        totalTime,
      );
      console.log("[Practice] Session completed:", completedSession);

      // Check if this is a personal best
      if (completedSession.is_personal_best !== undefined) {
        setIsPersonalBest(completedSession.is_personal_best);
        setPreviousBest(completedSession.previous_best || 0);
      }

      // Capture XP status
      if (completedSession.xp_earned !== undefined) {
        setXpEarned(completedSession.xp_earned || 0);
      }
      if (completedSession.xp_awarded !== undefined) {
        setXpAwarded(completedSession.xp_awarded);
      }
      if (completedSession.daily_xp_limit_reached !== undefined) {
        setDailyXpLimitReached(completedSession.daily_xp_limit_reached);
      }

      // If this practice was part of a lesson (forge/challenge), mark the lesson as complete
      if (fromLesson && lessonIdFromUrl && allAttempts.length > 0) {
        console.log("[Practice] Marking lesson as complete:", lessonIdFromUrl);
        try {
          // Use allAttempts (from ref) for accurate counts
          const lessonAttempted = allAttempts.length;
          const lessonCorrect = allAttempts.filter((a) => a.isCorrect).length;
          const lessonTime = Math.floor(
            allAttempts.reduce((sum, a) => sum + a.timeTaken, 0),
          );

          console.log(
            `[Practice] Submitting lesson results: ${lessonCorrect}/${lessonAttempted} correct in ${lessonTime}s`,
          );

          await api.submitLessonAnswer(
            lessonIdFromUrl,
            lessonCorrect,
            lessonTime,
            lessonAttempted,
          );
          console.log("[Practice] Lesson completion recorded");
        } catch (lessonError) {
          console.error(
            "[Practice] Failed to mark lesson complete:",
            lessonError,
          );
          // Don't fail the whole session if lesson update fails
        }
      }

      // Check for mastery level increase and trigger seal celebration
      if (selectedTechnique && (mode === "technique_drill" || mode === "beat_the_clock")) {
        try {
          console.log("[Practice] Checking for mastery level increase...");
          const updatedProgress = await api.getUserProgress();
          const newProgress = updatedProgress.find(p => p.technique_id === selectedTechnique);
          const newMasteryLevel = newProgress?.mastery_level || "none";

          console.log("[Practice] Mastery check:", {
            initial: initialMasteryLevel,
            new: newMasteryLevel
          });

          // Trigger celebration if mastery increased to advanced or expert
          if (newMasteryLevel === "advanced" || newMasteryLevel === "expert") {
            if (initialMasteryLevel !== newMasteryLevel) {
              const techniqueName = techniques.find(t => t.id === selectedTechnique)?.name || "Ancient Form";
              console.log("[Practice] CHAOS SOURCE SEALED! Technique:", techniqueName);
              setSealedTechniqueName(techniqueName);
              setShowSealCelebration(true);
            }
          }
        } catch (progressError) {
          console.error("[Practice] Failed to check mastery level:", progressError);
          // Don't fail session if progress check fails
        }
      }

      // Log practice for school students (fire-and-forget for module assignment tracking)
      if (user?.school_id && selectedTechnique) {
        try {
          const accuracy = totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0;
          console.log("[Practice] Logging practice for school student:", {
            technique_id: selectedTechnique,
            session_id: session?.id,
            problems_attempted: totalAttempted,
            accuracy,
          });
          await api.logPractice({
            technique_id: selectedTechnique,
            source: "training_hall",
            session_id: session?.id,
            problems_attempted: totalAttempted,
            accuracy,
          });
          console.log("[Practice] School practice logged successfully");
        } catch (logErr) {
          // Non-blocking: don't fail the session if practice logging fails
          console.error("[Practice] Failed to log practice for school:", logErr);
        }
      }

      // Show results screen instead of clearing immediately
      setShowResults(true);
      setSession(null);
      setCurrentProblem(null);
    } catch (error) {
      console.error("[Practice] Failed to end session:", error);
      // Still show results even if API fails
      setShowResults(true);
      setSession(null);
      setCurrentProblem(null);
    }
  };

  const resetPractice = () => {
    setShowResults(false);
    setCombo(0);
    setProblemAttempts([]);
    problemAttemptsRef.current = []; // Clear ref too
    setSession(null);
    setCurrentProblem(null);
    setXpEarned(0);
    setXpAwarded(true);
    setDailyXpLimitReached(false);
    setUserAnswer("");
    setFeedback(null);
    setProblemQueue([]);
    setCurrentProblemIndex(0);
    setIsPersonalBest(false);
    setPreviousBest(0);
    seenQuestions.current.clear();
    setShowSealCelebration(false);
    setSealedTechniqueName("");
    setInitialMasteryLevel(null);
  };

  // Results screen
  if (showResults) {
    const totalAttempts = problemAttempts.length;
    const correctAnswers = problemAttempts.filter((a) => a.isCorrect).length;
    const accuracy =
      totalAttempts > 0
        ? Math.round((correctAnswers / totalAttempts) * 100)
        : 0;
    const totalTime = problemAttempts.reduce((sum, a) => sum + a.timeTaken, 0);
    const avgTime =
      totalAttempts > 0 ? (totalTime / totalAttempts).toFixed(1) : 0;

    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl text-center">
              Training Complete! 🎉
            </CardTitle>
            {isPersonalBest && (
              <div className="mt-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg animate-in fade-in slide-in-from-top-4 duration-700">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl animate-bounce">🏆</span>
                  <div className="animate-in zoom-in-50 duration-500 delay-200">
                    <h3 className="text-2xl font-bold text-yellow-700">
                      New Personal Best!
                    </h3>
                    <p className="text-sm text-yellow-600">
                      You scored {correctAnswers} correct answers
                      {previousBest > 0 && ` (previous best: ${previousBest})`}
                    </p>
                  </div>
                  <span
                    className="text-4xl animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  >
                    🎉
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {totalAttempts}
                </div>
                <div className="text-sm text-gray-600">Problems</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {correctAnswers}
                </div>
                <div className="text-sm text-gray-600">Correct</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {accuracy}%
                </div>
                <div className="text-sm text-gray-600">Accuracy</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {avgTime}s
                </div>
                <div className="text-sm text-gray-600">Avg Time</div>
              </div>
            </div>

            {/* XP Status Message */}
            {xpAwarded && xpEarned > 0 && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-400">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl">✨</span>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-green-700">
                      XP Earned!
                    </h3>
                    <p className="text-2xl font-bold text-green-600">
                      +{xpEarned} XP
                    </p>
                  </div>
                  <span className="text-3xl">🎯</span>
                </div>
              </div>
            )}

            {dailyXpLimitReached && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-400">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl">⏰</span>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-blue-700">
                      Daily XP Already Earned
                    </h3>
                    <p className="text-sm text-blue-600">
                      Great practice! You've already earned XP for this
                      technique today.
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      Come back tomorrow for more XP rewards!
                    </p>
                  </div>
                  <span className="text-3xl">📚</span>
                </div>
              </div>
            )}

            {mode === "mixed_challenge" && masteredTechniques.length > 0 && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-lg mb-3 text-blue-900">
                  Your Mastered Techniques:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {masteredTechniques.map((tech) => {
                    const techniqueInfo = techniques.find(
                      (t) => t.id === tech.technique_id,
                    );
                    const masteryColor =
                      tech.mastery_level === "expert"
                        ? "bg-purple-100 text-purple-800 border-purple-300"
                        : tech.mastery_level === "advanced"
                          ? "bg-green-100 text-green-800 border-green-300"
                          : "bg-blue-100 text-blue-800 border-blue-300";

                    return (
                      <div
                        key={tech.technique_id}
                        className={`p-2 rounded border ${masteryColor}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">
                            {techniqueInfo?.name || "Unknown Technique"}
                          </span>
                          <span className="text-xs font-semibold uppercase">
                            {tech.mastery_level}
                          </span>
                        </div>
                        <div className="text-xs mt-1">
                          {tech.correct_problems}/{tech.total_problems} correct
                          (
                          {Math.round(
                            (tech.correct_problems / tech.total_problems) *
                              100,
                          )}
                          %)
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <h3 className="font-semibold text-lg mb-3">
                Review Your Answers:
              </h3>
              {problemAttempts.map((attempt, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    attempt.isCorrect
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-700">
                        #{index + 1}
                      </span>
                      {attempt.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {attempt.timeTaken.toFixed(1)}s
                    </span>
                  </div>
                  <div className="text-xl font-mono mb-2">
                    {attempt.problem.question}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Your answer: </span>
                      <span
                        className={
                          attempt.isCorrect
                            ? "text-green-700 font-semibold"
                            : "text-red-700 font-semibold"
                        }
                      >
                        {attempt.userAnswer || "(no answer)"}
                      </span>
                    </div>
                    {!attempt.isCorrect && (
                      <div>
                        <span className="text-gray-600">Correct answer: </span>
                        <span className="text-green-700 font-semibold">
                          {attempt.correctAnswer}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              {fromLesson ? (
                <>
                  <Button
                    onClick={() => navigate(returnToFromUrl || "/arena")}
                    className="flex-1"
                    size="lg"
                  >
                    Back to Curriculum
                  </Button>
                  <Button
                    onClick={resetPractice}
                    variant="outline"
                    className="flex-1"
                    size="lg"
                  >
                    Practice Again
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={resetPractice} className="flex-1" size="lg">
                    Start New Session
                  </Button>
                  <Button
                    onClick={() => (window.location.href = "/dashboard")}
                    variant="outline"
                    className="flex-1"
                    size="lg"
                  >
                    Back to Dashboard
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Training Hall
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Choose your training mode and begin
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Start Training Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Training Mode</label>
              <div className="grid grid-cols-1 gap-3">
                <Button
                  variant={mode === "technique_drill" ? "default" : "outline"}
                  className="h-auto py-4 flex flex-col items-start gap-1"
                  onClick={() => setMode("technique_drill")}
                >
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    <span className="font-semibold">Form Practice</span>
                  </div>
                  <span className="text-xs text-left opacity-80">
                    Train with one Ancient Form
                  </span>
                </Button>

                <Button
                  variant={mode === "mixed_challenge" ? "default" : "outline"}
                  className="h-auto py-4 flex flex-col items-start gap-1"
                  onClick={() => {
                    if (featureFlags.PREMIUM_PRACTICE_MODES && !isPremiumUser) {
                      setUpgradeFeatureName("Combined Forms");
                      setShowUpgradeDialog(true);
                      return;
                    }
                    setMode("mixed_challenge");
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Shuffle className="w-5 h-5" />
                    <span className="font-semibold">Combined Forms</span>
                    {featureFlags.SHOW_PRO_BADGES &&
                      featureFlags.PREMIUM_PRACTICE_MODES &&
                      !isPremiumUser && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-500 text-white rounded">
                          PRO
                        </span>
                      )}
                  </div>
                  <span className="text-xs text-left opacity-80">
                    Random problems from different Ancient Forms
                  </span>
                </Button>

                <Button
                  variant={mode === "beat_the_clock" ? "default" : "outline"}
                  className="h-auto py-4 flex flex-col items-start gap-1"
                  onClick={() => {
                    if (featureFlags.PREMIUM_PRACTICE_MODES && !isPremiumUser) {
                      setUpgradeFeatureName("Time Trial");
                      setShowUpgradeDialog(true);
                      return;
                    }
                    setMode("beat_the_clock");
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5" />
                    <span className="font-semibold">Time Trial</span>
                    {featureFlags.SHOW_PRO_BADGES &&
                      featureFlags.PREMIUM_PRACTICE_MODES &&
                      !isPremiumUser && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-500 text-white rounded">
                          PRO
                        </span>
                      )}
                  </div>
                  <span className="text-xs text-left opacity-80">
                    2-minute timed challenge
                  </span>
                </Button>

                <Button
                  variant={mode === "sample_papers" ? "default" : "outline"}
                  className="h-auto py-4 flex flex-col items-start gap-1"
                  onClick={() => navigate("/sample-papers")}
                >
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    <span className="font-semibold">
                      Math Olympiad Sample Papers
                    </span>
                  </div>
                  <span className="text-xs text-left opacity-80">
                    Practice with competition-style papers
                  </span>
                </Button>
              </div>
            </div>

            {(mode === "technique_drill" || mode === "beat_the_clock") && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Ancient Form</label>
                {availableTechniques.length === 0 && !isTeacher ? (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg space-y-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      🗺️ Your training starts in The Arena! Finish a lesson
                      there to unlock techniques you can practice here.
                    </p>
                    <Button size="sm" onClick={() => navigate("/arena")}>
                      Go to The Arena
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <Popover
                    open={techniqueComboOpen}
                    onOpenChange={setTechniqueComboOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={techniqueComboOpen}
                        className="w-full justify-between"
                      >
                        {selectedTechnique
                          ? availableTechniques.find(
                              (t) => t.id === selectedTechnique,
                            )?.name
                          : "Choose a technique"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search techniques..." />
                        <CommandList>
                          <CommandEmpty>No technique found.</CommandEmpty>
                          <CommandGroup>
                            {availableTechniques.map((technique) => (
                              <CommandItem
                                key={technique.id}
                                value={technique.name}
                                onSelect={() => {
                                  setSelectedTechnique(technique.id);
                                  setTechniqueComboOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedTechnique === technique.id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                                {technique.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}

            {mode === "technique_drill" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Number of Problems
                </label>
                <Select
                  value={String(problemCount)}
                  onValueChange={(v) => setProblemCount(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 Problems</SelectItem>
                    <SelectItem value="20">20 Problems</SelectItem>
                    <SelectItem value="30">30 Problems</SelectItem>
                    <SelectItem value="50">50 Problems</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              onClick={startSession}
              disabled={
                ((mode === "technique_drill" || mode === "beat_the_clock") &&
                  !selectedTechnique) ||
                ((mode === "technique_drill" || mode === "beat_the_clock") &&
                  availableTechniques.length === 0 &&
                  !isTeacher)
              }
            >
              Begin Training
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentTechniqueName =
    (mode === "technique_drill" || mode === "beat_the_clock") &&
    selectedTechnique
      ? techniques.find((t) => t.id === selectedTechnique)?.name
      : null;

  // Check if this is a Yes/No question based on problem type
  // Fallback to checking answer if type is not specified (for backwards compatibility)
  const isYesNoQuestion =
    currentProblem?.type === "yes_no" ||
    (!currentProblem?.type &&
      (currentProblem?.answer?.toLowerCase().trim() === "yes" ||
        currentProblem?.answer?.toLowerCase().trim() === "no"));

  // Show the numeric keypad on tablets/phones when the answer is digits-only,
  // and the decimal keypad when it contains . or / (answers with letters,
  // minus signs, or % still need the full keyboard)
  const answerText = currentProblem?.answer?.trim() ?? "";
  const numericOnlyAnswer = /^[0-9,\s]+$/.test(answerText);
  const decimalAnswer = /^[0-9,.\s/]+$/.test(answerText);

  // Multiple choice problems render tappable options instead of an input
  const isMultipleChoice =
    currentProblem?.type === "multiple_choice" &&
    (currentProblem?.options?.length ?? 0) > 0;

  const handleChoiceAnswer = (option: string) => {
    setUserAnswer(option);
    // Submit immediately - option is passed directly so no race condition
    submitAnswer(option);
  };

  const handleYesNoAnswer = (answer: string) => {
    setUserAnswer(answer);
    // Submit immediately - answer is passed directly so no race condition
    submitAnswer(answer);
  };

  console.log(
    "[Practice] Render - session:",
    !!session,
    "currentProblem:",
    !!currentProblem,
    "loadingProblem:",
    loadingProblem,
    "showResults:",
    showResults,
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Premium Upgrade Dialog */}
      <PremiumUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        feature={upgradeFeatureName}
      />

      {/* Seal Celebration Modal */}
      {showSealCelebration && (
        <SealCelebration
          show={showSealCelebration}
          techniqueName={sealedTechniqueName}
          xpEarned={xpEarned}
          onClose={() => setShowSealCelebration(false)}
        />
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            {mode === "technique_drill" && "Form Practice"}
            {mode === "mixed_challenge" && "Combined Forms"}
            {mode === "beat_the_clock" && "Time Trial"}
          </h2>
          {currentTechniqueName && (
            <p className="text-lg font-semibold text-blue-600 mb-1">
              {currentTechniqueName}
            </p>
          )}
          <p className="text-gray-600">
            {(mode === "technique_drill" || mode === "beat_the_clock") &&
            problemQueue.length > 0 ? (
              <>
                Problem {currentProblemIndex + 1}/{problemQueue.length}
              </>
            ) : (
              <>
                Problems: {session.problems_attempted}/
                {mode === "technique_drill" ? problemCount : "∞"}
              </>
            )}
            {" | "}
            Correct: {session.problems_correct}
          </p>
        </div>
        {mode === "beat_the_clock" && (
          <div className="text-2xl font-bold text-orange-600">
            {Math.floor(timeRemaining / 60)}:
            {(timeRemaining % 60).toString().padStart(2, "0")}
          </div>
        )}
      </div>

      {loadingProblem ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading problem...</p>
            </div>
          </CardContent>
        </Card>
      ) : currentProblem ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-6xl font-mono">
              {currentProblem.question}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {feedback ? (
              feedback.correct ? (
                <div className="p-6 rounded-2xl text-center bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 animate-pop-in">
                  <CheckCircle className="w-16 h-16 mx-auto mb-3 text-green-600" />
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {feedback.message}
                  </p>
                  {combo >= 2 && (
                    <p className="text-lg font-bold text-orange-500 mt-2">
                      🔥 {combo} in a row!
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-6 rounded-2xl text-center bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 animate-pop-in">
                  <p className="text-xl font-semibold text-amber-800 dark:text-amber-200 mb-2">
                    {feedback.message}
                  </p>
                  <p className="text-5xl font-mono font-bold text-gray-900 dark:text-white mb-6">
                    {feedback.correctAnswer}
                  </p>
                  <Button
                    size="lg"
                    className="w-full h-14 text-lg"
                    autoFocus
                    onClick={continueAfterWrong}
                  >
                    Keep Going
                    <ArrowRight className="w-5 h-5 ml-1" />
                  </Button>
                </div>
              )
            ) : (
              <>
                {isMultipleChoice ? (
                  /* Multiple choice options (SAT-style) */
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentProblem.options!.map((option, index) => (
                      <Button
                        key={index}
                        variant={userAnswer === option ? "default" : "outline"}
                        size="lg"
                        className="h-16 text-2xl font-mono justify-start gap-3"
                        onClick={() => handleChoiceAnswer(option)}
                        disabled={submitting}
                      >
                        <span className="text-sm font-sans font-semibold text-gray-400">
                          {String.fromCharCode(65 + index)}
                        </span>
                        {option}
                      </Button>
                    ))}
                  </div>
                ) : isYesNoQuestion ? (
                  /* Yes/No Buttons for binary questions */
                  <div className="flex gap-4">
                    <Button
                      variant={
                        userAnswer.toLowerCase() === "yes"
                          ? "default"
                          : "outline"
                      }
                      size="lg"
                      className="flex-1 h-20 text-xl"
                      onClick={() => handleYesNoAnswer("Yes")}
                      disabled={submitting}
                    >
                      <Check className="w-6 h-6 mr-2" />
                      Yes
                    </Button>
                    <Button
                      variant={
                        userAnswer.toLowerCase() === "no"
                          ? "default"
                          : "outline"
                      }
                      size="lg"
                      className="flex-1 h-20 text-xl"
                      onClick={() => handleYesNoAnswer("No")}
                      disabled={submitting}
                    >
                      <X className="w-6 h-6 mr-2" />
                      No
                    </Button>
                  </div>
                ) : (
                  /* Regular text input for numeric/other answers */
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Your Answer</label>
                      <Input
                        type="text"
                        inputMode={
                          numericOnlyAnswer
                            ? "numeric"
                            : decimalAnswer
                              ? "decimal"
                              : "text"
                        }
                        value={userAnswer}
                        onChange={(e) => {
                          // Allow alphanumerics, -, %, comma, and the
                          // grid-in characters . and / (decimals, fractions)
                          const filteredValue = e.target.value.replace(
                            /[^a-zA-Z0-9\-,%./]/g,
                            "",
                          );
                          setUserAnswer(filteredValue);
                        }}
                        onKeyPress={(e) =>
                          e.key === "Enter" &&
                          !submitting &&
                          userAnswer.trim() &&
                          submitAnswer()
                        }
                        // placeholder="Enter your answer"
                        className="!text-6xl text-center font-mono h-24 py-6 placeholder:!text-6xl"
                        autoFocus
                        ref={answerInputRef}
                        disabled={submitting}
                      />
                    </div>
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={() => submitAnswer()}
                      disabled={submitting || !userAnswer.trim()}
                    >
                      {submitting ? "Submitting..." : "Submit Answer"}
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {!fromLesson && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={handleEndSession}>
            End Session
          </Button>
        </div>
      )}
    </div>
  );
}
